import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuClipboardX } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';

/**
 * Screen fail visits of a project with the staff member's name. Names come
 * from the project's own employees list first (raw document), then from
 * Employees for anyone not found there.
 */
const fetchScreenFails = async (projectId) => {
  const projectDoc = await getDoc(doc(db, 'Projects', projectId));
  const projectData = projectDoc.exists() ? projectDoc.data() : {};
  const projectEmployees = Array.isArray(projectData.employees) ? projectData.employees : [];
  const empNameMap = {};
  projectEmployees.forEach((emp) => {
    if (emp && emp.id) empNameMap[emp.id] = `${emp.name || emp.eName || ''} ${emp.surname || emp.eSurname || ''}`.trim();
  });

  const snapshot = await getDocs(query(collection(db, 'Visits'), where('projectId', '==', projectId), where('screenFail', '==', true)));
  const screenFails = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  const unresolved = [...new Set(screenFails.map((sf) => sf.employeeId).filter((id) => id && !empNameMap[id]))];
  await Promise.all(unresolved.map(async (empId) => {
    try {
      const empDoc = await getDoc(doc(db, 'Employees', empId));
      if (empDoc.exists()) {
        const data = empDoc.data();
        empNameMap[empId] = `${data.eName || ''} ${data.eSurname || ''}`.trim();
      }
    } catch (error) {
      console.error('Error fetching employee:', empId, error);
    }
  }));

  return screenFails.map((sf) => ({ ...sf, employeeFullName: empNameMap[sf.employeeId] || sf.employeeName || sf.employeeId || '-' }));
};

const CELL = 'tw-whitespace-nowrap tw-border-b tw-border-slate-100 tw-px-3 tw-py-2.5 tw-text-left tw-align-middle dark:tw-border-white/[0.06]';
const HEAD = 'tw-sticky tw-top-0 tw-min-w-0 tw-whitespace-nowrap tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-[#262a34] dark:tw-text-slate-400';

/** "Screen Fail": the project's screen fail records with a short summary. */
const ScreenFailModal = ({ project, onClose }) => {
  const { t, language, locale } = useTranslation();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    fetchScreenFails(project.id)
      .then((result) => !cancelled && setRows(result))
      .catch((error) => {
        console.error('Error fetching screen fail data:', error);
        toast.error(t('projectManagement.toast.screenFailError'));
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
    // Reload for another project only, not when the language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const summary = useMemo(() => (rows ? [
    { key: 'total', label: t('projectManagement.screenFail.total'), value: rows.length },
    { key: 'employees', label: t('projectManagement.screenFail.employees'), value: new Set(rows.map((sf) => sf.employeeId)).size },
    { key: 'patients', label: t('projectManagement.screenFail.patients'), value: new Set(rows.map((sf) => sf.patientId).filter((p) => p && p !== '-')).size },
  ] : []), [rows, t]);

  let body;
  if (rows === null) {
    body = (
      <div aria-label={t('projectManagement.screenFail.loading')} className="tw-space-y-3">
        <div className="tw-grid tw-grid-cols-3 tw-gap-2.5">
          {[0, 1, 2].map((index) => <span key={index} className="tw-block tw-h-16 tw-animate-pulse tw-rounded-2xl tw-bg-slate-200/70 dark:tw-bg-white/[0.06]" />)}
        </div>
        <span className="tw-block tw-h-40 tw-animate-pulse tw-rounded-2xl tw-bg-slate-200/50 dark:tw-bg-white/[0.04]" />
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center">
        <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
          <LuClipboardX aria-hidden="true" className="tw-h-7 tw-w-7" />
        </span>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('projectManagement.screenFail.empty')}</p>
      </div>
    );
  } else {
    body = (
      <div className="tw-space-y-4">
        <dl className="tw-grid tw-grid-cols-3 tw-gap-2.5">
          {summary.map((item) => (
            <div key={item.key} className="tw-rounded-2xl tw-border tw-border-rose-500/15 tw-bg-rose-500/[0.06] tw-px-3 tw-py-3 dark:tw-border-rose-400/15">
              <dt className="tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{item.label}</dt>
              <dd className="tw-mt-0.5 tw-text-2xl tw-font-semibold tw-text-rose-700 tw-tabular-nums dark:tw-text-rose-300">{item.value.toLocaleString(locale)}</dd>
            </div>
          ))}
        </dl>
        <div className="tw-max-h-[50vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 glass-scrollbar dark:tw-border-white/10">
          <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-text-sm">
            <thead className="tw-bg-transparent">
              <tr className="tw-bg-transparent">
                <th scope="col" className={HEAD}>{t('projectManagement.screenFail.date')}</th>
                <th scope="col" className={HEAD}>{t('projectManagement.screenFail.employee')}</th>
                <th scope="col" className={HEAD}>{t('projectManagement.screenFail.patientId')}</th>
                <th scope="col" className={HEAD}>{t('projectManagement.screenFail.visitNumber')}</th>
                <th scope="col" className={HEAD}>{t('projectManagement.screenFail.info')}</th>
              </tr>
            </thead>
            <tbody className="tw-bg-transparent">
              {rows.map((sf) => (
                <tr key={sf.id} className="tw-bg-transparent tw-text-slate-700 dark:tw-text-slate-200">
                  <td className={`${CELL} tw-tabular-nums`}>{sf.firstVisitDate || sf.date || '-'}</td>
                  <td className={`${CELL} tw-font-medium`}>{sf.employeeFullName}</td>
                  <td className={`${CELL} tw-tabular-nums`}>{sf.patientId || '-'}</td>
                  <td className={`${CELL} tw-tabular-nums`}>{sf.visitNumber || '-'}</td>
                  <td className={`${CELL} tw-max-w-[16rem] tw-truncate`} title={sf.info || undefined}>{sf.info || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={t('projectManagement.screenFail.title')}
      subtitle={project.titleShort || project.customer}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30" size="xs">
          <LuClipboardX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
    >
      {body}
    </Modal>
  );
};

ScreenFailModal.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ScreenFailModal;
